import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bookmark, Sparkles, ArrowRight, Pin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROUTE_LABEL = {
  '/work-orders': 'Work Orders',
  '/assets': 'Assets',
  '/inspections': 'Inspections',
  '/operations': 'Operations',
  '/vendors': 'Vendors',
  '/ppm': 'Preventive Maintenance',
  '/budget': 'Budget',
};

/**
 * Dashboard widget surfacing the user's pinned views (with live match counts)
 * + top-3 most-used saved views as one-click cards. Pinned views always come
 * first so users get true, hand-picked at-a-glance counts.
 */
const SavedViewsWidget = () => {
  const navigate = useNavigate();
  const [pinned, setPinned] = useState([]);
  const [topUsed, setTopUsed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [pinRes, topRes] = await Promise.all([
          axios.get(`${API_URL}/api/saved-views/pinned`, { headers }),
          axios.get(`${API_URL}/api/saved-views/top?limit=3`, { headers }),
        ]);
        setPinned(Array.isArray(pinRes.data) ? pinRes.data : []);
        setTopUsed(Array.isArray(topRes.data) ? topRes.data : []);
      } catch (err) {
        logger.debug('saved views widget fetch failed', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const apply = (view) => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(`${API_URL}/api/saved-views/${view.id}/access`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch((err) => logger.debug('access bump failed', err));
    }
    navigate(`${view.path}${view.query_string ? `?${view.query_string}` : ''}`);
  };

  return (
    <Card className="card-dashboard h-full" data-testid="saved-views-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Bookmark className="h-5 w-5 text-[#111565]" />
          Your views
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-12 rounded-md bg-muted/50 animate-pulse" />)}
          </div>
        ) : pinned.length === 0 && topUsed.length === 0 ? (
          <div className="text-center py-6">
            <Sparkles className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-xs text-muted-foreground">
              Pin a saved view from the Views menu to see live match counts here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {pinned.length > 0 && (
              <div className="space-y-1.5" data-testid="saved-views-widget-pinned">
                {pinned.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => apply(v)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md border border-[#ff6600]/30 bg-[#ff6600]/5 hover:bg-[#ff6600]/10 transition text-left group"
                    data-testid={`saved-views-widget-pinned-${v.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <Pin className="h-3 w-3 text-[#ff6600] flex-shrink-0" />
                        <span className="text-sm font-medium truncate">{v.name}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {ROUTE_LABEL[v.path] || v.path} · <strong>{v.match_count} match{v.match_count === 1 ? '' : 'es'}</strong>
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground/60 flex-shrink-0 group-hover:text-[#ff6600] group-hover:translate-x-0.5 transition" />
                  </button>
                ))}
              </div>
            )}

            {topUsed.length > 0 && (
              <div className="space-y-1.5">
                {pinned.length > 0 && (
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold pt-1">
                    Most used
                  </p>
                )}
                {topUsed.map((v, idx) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => apply(v)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md border border-transparent hover:border-[#111565]/20 hover:bg-[#111565]/5 transition text-left group"
                    data-testid={`saved-views-widget-card-${v.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {idx === 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-semibold uppercase tracking-wide flex-shrink-0"
                            title={`Used ${v.access_count} times`}
                          >
                            <Sparkles className="h-2.5 w-2.5" /> Top
                          </span>
                        )}
                        <span className="text-sm font-medium truncate">{v.name}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {ROUTE_LABEL[v.path] || v.path}
                        {v.access_count > 0 && ` · used ${v.access_count}×`}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground/60 flex-shrink-0 group-hover:text-[#111565] group-hover:translate-x-0.5 transition" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SavedViewsWidget;
